/**
 * 청구서별 계약자(조직) 조회 훅
 *
 * 세금계산서 발행 가능 여부는 계약의 계약자가 아니라 각 청구서에 저장된 계약자(`bill.organizationId`)로 판정해야 한다.
 * 청구서의 계약자는 발행 시점 값으로 고정되므로, 계약 중간에 계약자를 A -> B 로 바꾸면
 * 과거 청구서는 A(또는 계약자 없음), 미발행 청구서는 B 가 되어 계약의 현재 계약자와 달라진다.
 *
 * 목록에 있는 계약자 id 를 모아 한 번에 조회해서 Map 으로 돌려준다.
 */
import { useEffect, useMemo, useState } from "react";
import { ScheduleRoundNOverdueItem } from "src/api/billingSchedule/billing-type";
import { useApiOperation } from "src/api/hooks";
import { getOrgList } from "src/api/organization/org-api";
import { MbOrg } from "src/api/organization/org-types";

export type BillOrganizationMap = Map<number, MbOrg>;

const useBillOrganizations = (roundList?: ScheduleRoundNOverdueItem[]): BillOrganizationMap => {
  const { executeAsync: getOrgListApi } = useApiOperation(getOrgList);
  const [billOrganizationMap, setBillOrganizationMap] = useState<BillOrganizationMap>(new Map());

  // 청구서에 담긴 계약자 id 중 유효한 값만 중복 제거. 값이 없는 청구서(백필 이전/계약자 미등록)는 제외된다
  const organizationIds = useMemo(() => {
    const ids = (roundList ?? []).map((item) => Number(item?.bill?.organizationId ?? 0)).filter((id) => id > 0);
    return Array.from(new Set(ids)).sort((a, b) => a - b);
  }, [roundList]);

  const organizationIdsKey = organizationIds.join(",");

  useEffect(() => {
    if (organizationIdsKey === "") {
      setBillOrganizationMap(new Map());
      return;
    }

    let isValid = true;

    const fetchOrganizations = async () => {
      const orgList = await getOrgListApi({ id: organizationIdsKey, size: organizationIdsKey.split(",").length })
        .then(({ data }) => data?.data?.content ?? [])
        .catch(() => [] as MbOrg[]);

      if (!isValid) return;
      setBillOrganizationMap(new Map(orgList.map((org) => [Number(org.id), org])));
    };

    fetchOrganizations();

    return () => {
      isValid = false;
    };
  }, [organizationIdsKey, getOrgListApi]);

  return billOrganizationMap;
};

export default useBillOrganizations;
